import React, { useState, useRef, useEffect } from "react";
import styles from "./VideoPlayer.module.scss";
import DotLoader from "./DotLoader";

const VideoPlayer = ({ src, autoPlay = false, loop = false, poster, splashBg, splashLogo, splashDuration = 2000, className }) => {
    const hasSplash = !!(splashBg || splashLogo);
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const [isLoading, setIsLoading] = useState(!!autoPlay && !hasSplash);
    const [showSplash, setShowSplash] = useState(hasSplash);
    const retryRef = useRef(null);
    const splashTimerRef = useRef(null);

    // Splash: show for splashDuration, then fade out and start video
    useEffect(() => {
        if (!hasSplash) return;

        splashTimerRef.current = setTimeout(() => {
            setShowSplash(false);
        }, splashDuration);

        return () => {
            if (splashTimerRef.current) clearTimeout(splashTimerRef.current);
        };
    }, [hasSplash, splashDuration]);

    // Start video when splash finishes (or immediately if no splash)
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !autoPlay) return;
        if (hasSplash && showSplash) return; // Wait for splash to finish

        setIsLoading(true);

        const tryPlay = () => {
            video.play()
                .then(() => {
                    setIsPlaying(true);
                    setIsLoading(false);
                })
                .catch(() => {
                    retryRef.current = setTimeout(() => {
                        video.play()
                            .then(() => {
                                setIsPlaying(true);
                                setIsLoading(false);
                            })
                            .catch(() => setIsLoading(false));
                    }, 500);
                });
        };

        if (video.readyState >= 2) {
            tryPlay();
        } else {
            video.addEventListener('canplay', tryPlay, { once: true });
            return () => {
                video.removeEventListener('canplay', tryPlay);
                if (retryRef.current) clearTimeout(retryRef.current);
            };
        }

        return () => {
            if (retryRef.current) clearTimeout(retryRef.current);
        };
    }, [autoPlay, src, showSplash, hasSplash]);

    // Sync state with video events
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const onPlay = () => { setIsPlaying(true); setIsLoading(false); };
        const onPause = () => setIsPlaying(false);
        const onEnded = () => {
            setIsPlaying(false);
            setProgress(100);
        };
        const onTimeUpdate = () => {
            if (video.duration) {
                setProgress((video.currentTime / video.duration) * 100);
            }
        };
        const onWaiting = () => setIsLoading(true);
        const onPlaying = () => setIsLoading(false);

        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('ended', onEnded);
        video.addEventListener('timeupdate', onTimeUpdate);
        video.addEventListener('waiting', onWaiting);
        video.addEventListener('playing', onPlaying);

        return () => {
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('ended', onEnded);
            video.removeEventListener('timeupdate', onTimeUpdate);
            video.removeEventListener('waiting', onWaiting);
            video.removeEventListener('playing', onPlaying);
        };
    }, []);

    const togglePlay = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
        setIsMuted(video.muted);
    };

    const handleProgressClick = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        const bar = e.currentTarget;
        if (!video || !bar || !video.duration) return;
        const rect = bar.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        video.currentTime = percent * video.duration;
        setProgress(percent * 100);
    };

    return (
        <div className={`${styles.videoWrapper} ${className || ''}`}>
            <video
                ref={videoRef}
                src={src}
                muted={isMuted}
                loop={loop}
                playsInline
                preload="auto"
                poster={poster}
                className={styles.videoElement}
            />
            {hasSplash && (
                <div className={`${styles.splashOverlay} ${!showSplash ? styles.splashFadeOut : ''}`}>
                    {splashBg && <img src={splashBg} alt="" className={styles.splashBg} />}
                    {splashLogo && <img src={splashLogo} alt="" className={styles.splashLogo} />}
                </div>
            )}
            {isLoading && !showSplash && (
                <div className={styles.loadingOverlay}>
                    <DotLoader size="medium" variant="light" />
                </div>
            )}
            <div className={styles.videoControls}>
                <div className={styles.controlsRow}>
                    <div className={styles.controlsLeft}>
                        <button className={styles.circleBtn} onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
                            {isPlaying ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16" rx="1" />
                                    <rect x="14" y="4" width="4" height="16" rx="1" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            )}
                        </button>
                    </div>
                    <div className={styles.controlsRight}>
                        <button className={styles.circleBtn} onClick={toggleMute} aria-label={isMuted ? "Unmute" : "Mute"}>
                            {isMuted ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <div className={styles.progressBar} onClick={handleProgressClick}>
                <div className={styles.progressTrack} />
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
            </div>
        </div>
    );
};

export default VideoPlayer;
